//
// Copyright (c) 2025 Comcast
//

import transform from 'lodash/transform'
import isEqual from 'lodash/isEqual'
import isObject from 'lodash/isObject'
import isArray from 'lodash/isArray'
import isPlainObject from 'lodash/isPlainObject'
import forOwn from 'lodash/forOwn'

// Performs a deep diff between two objects.
//   object Object compared
//   base   Object to compare with
//   options Object:
//     walkArrays Boolean, false by defaul. If true, walks arrays and
//                only different elements of array are returned.
//                By default whole arrays will be in diff if any elements differ.
// Returns a new object which represents the diff
// Known issue:
//   if obj1 at any level has { key1: undefined } and key1 is absent in obj2 (or vice versa),
//   it will return obj2 = {} instead of { key1: undefined }.
//   The fix will make the function too complex.
//   It doesn't affect forms, just may be confusing in case of rare unit test errors.
export const diffObjectsDeep = (object, base, options = {}) => {
  const { walkArrays = false } = options
  const changes = (object, base) => {
    return transform(object, (result, value, key) => {
      const baseValue = base[key]
      if (isEqual(value, baseValue)) {
        return
      }
      const areObjects = isObject(value) && isObject(baseValue)
      const areArrays = isArray(value) && isArray(baseValue)

      const needDeepCompare = areObjects && (!areArrays || walkArrays)
      result[key] = needDeepCompare ? changes(value, baseValue) : value
    })
  }
  return changes(object, base)
}

// Recursively removes specified keys from an object or array.
//   object - The object or array to process
//   keysToRemove - array with keys to remove from the object.
// Returns a new object or array with the specified keys removed.
export const deepRemoveKeys = (object, keysToRemove = []) => {
  if (!isObject(object)) return object

  const newObject = isArray(object) ? [...object] : { ...object }

  forOwn(newObject, (value, key) => {
    if (keysToRemove.includes(key)) {
      delete newObject[key]
    } else if (isObject(value)) {
      newObject[key] = deepRemoveKeys(value, keysToRemove)
    }
  })

  return newObject
}

// Deep walk by object's leaves and call callback function fn(key, value, rootKey, level)
// rootKey is null or key of topmost parent
export const walkLeavesDeep = (object, fn) => {
  const walk = (object, fn, level, parentKeys) => {
    return forOwn(object, (value, key) => {
      if (isPlainObject(value)) {
        const newParentKeys = [...parentKeys]
        newParentKeys.push(key)
        walk(value, fn, level + 1, newParentKeys)
      } else {
        fn(key, value, level, parentKeys)
      }
    })
  }
  return walk(object, fn, 0, [])
}

// remove object elements with undefined values
export const removeUndefinedValues = (obj) => {
  Object.keys(obj).forEach(key => {
    obj[key] === undefined && delete obj[key]
  })
  return obj
}
